package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import kotlin.coroutines.resume

enum class LCodeLanguage {
    NONE,
    JSON,
}

val LCodeBlockStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toSitePalette().surface)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Card)
        .overflow(com.varabyte.kobweb.compose.css.Overflow.Auto)
}

/**
 * `surfaceSunken`, not `surfaceRaised` — this was the second flat fill using the floating-only
 * token as a container background, same defect as `LTable`'s row hover. `LCodeBlockStyle` already
 * fills the block itself with `surface`; a header that floats above it as `surfaceRaised` reads as
 * a card stacked on a card. `LTableHeadStyle` is the precedent for a flat header strip: recessed,
 * on `surfaceSunken`, the same ground `LTextField` and its siblings use for their own wells.
 */
val LCodeBlockHeaderStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(leftRight = Space.LG, topBottom = Space.SM)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .styleModifier { property("text-transform", "uppercase") }
}

val LCodeBlockPreStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .padding(Space.LG)
        .color(colorMode.toSitePalette().textPrimary)
        .fontSize(TypeScale.Small)
        .monoFont()
        .styleModifier {
            property("line-height", "1.7")
            property("tab-size", "2")
            property("white-space", "pre-wrap")
            property("overflow-wrap", "anywhere")
        }
}

internal const val CREDENTIAL_REFUSAL: String =
    "Redacted. This looks like a credential, and this component never shows one.\n" +
        "If you genuinely need the value it is in the database, not on a screen."

private val CREDENTIAL_SHAPES = listOf(
    Regex("""\b(lgo|lgm|lgvinv|lgvrst|lgvver|lgvchg)_[A-Za-z0-9_-]{20,}"""),
    Regex("""\b(ctm|sub|txn|pri|pro|inv|adj)_01[0-9a-hjkmnp-tv-z]{20,}""", RegexOption.IGNORE_CASE),
    Regex("""\bBearer\s+[A-Za-z0-9._-]{16,}""", RegexOption.IGNORE_CASE),
    Regex("""\bsk-(ant-)?[A-Za-z0-9_-]{16,}"""),
)

internal fun looksLikeCredential(text: String): Boolean = CREDENTIAL_SHAPES.any { it.containsMatchIn(text) }

private val PRETTY_JSON = Json { prettyPrint = true }

private fun sortJson(element: JsonElement): JsonElement = when (element) {
    is JsonObject -> JsonObject(element.entries.sortedBy { it.key }.associate { it.key to sortJson(it.value) })
    is JsonArray -> JsonArray(element.map(::sortJson))
    is JsonPrimitive -> element
}

internal fun renderCodeBlockText(text: String, language: LCodeLanguage): String {
    if (looksLikeCredential(text)) return CREDENTIAL_REFUSAL
    if (language != LCodeLanguage.JSON) return text
    return runCatching {
        PRETTY_JSON.encodeToString(JsonElement.serializer(), sortJson(Json.parseToJsonElement(text.trim())))
    }.getOrDefault(text)
}

private val DetailsElement = ElementBuilder.createBuilder<HTMLElement>("details")
private val SummaryElement = ElementBuilder.createBuilder<HTMLElement>("summary")

private data class CodeCopyAnnouncement(val attempt: Int, val message: String)

@Composable
fun LCodeBlock(
    text: String,
    label: String,
    modifier: Modifier = Modifier,
    language: LCodeLanguage = LCodeLanguage.NONE,
    maxLines: Int = 24,
    copyable: Boolean = true,
    onCopied: () -> Unit = {},
) {
    val rendered = renderCodeBlockText(text, language)
    val refused = rendered == CREDENTIAL_REFUSAL
    val canCopy = copyable && !refused
    var expanded by remember { mutableStateOf(false) }
    // A code change or copyability refusal retires the old attempt, including its fallback.
    // Keep these lifetimes keyed without remounting the code region or moving keyboard focus.
    val scope = key(rendered, canCopy) { rememberCoroutineScope() }
    val confirmation = key(rendered, canCopy) { rememberCopyConfirmation() }
    var copyJob by remember(scope) { mutableStateOf<Job?>(null) }
    var copyAttempt by remember(rendered, canCopy) { mutableStateOf(0) }
    var announcement by remember(rendered, canCopy) { mutableStateOf("") }
    var requestedAnnouncement by remember(rendered, canCopy) { mutableStateOf<CodeCopyAnnouncement?>(null) }
    var copyFailed by remember(rendered, canCopy) { mutableStateOf(false) }
    val copiedMessage = t("ds.ds.lcode_block.copied", "Copied.")
    val failedMessage = t(
        "ds.ds.lcode_block.copy_failed",
        "Couldn't copy. Select the code and copy it manually.",
    )
    val lines = rendered.count { it == '\n' } + 1

    fun announce(attempt: Int, message: String) {
        announcement = ""
        requestedAnnouncement = CodeCopyAnnouncement(attempt, message)
    }

    LaunchedEffect(rendered, canCopy, copyAttempt, requestedAnnouncement) {
        val request = requestedAnnouncement ?: return@LaunchedEffect
        if (request.attempt != copyAttempt) return@LaunchedEffect
        // This effect starts after the empty region commits. Wait one browser frame before
        // inserting the same sentence again, rather than relying on spoken whitespace changes.
        suspendCancellableCoroutine<Unit> { continuation ->
            val frame = window.requestAnimationFrame {
                if (continuation.isActive) continuation.resume(Unit)
            }
            continuation.invokeOnCancellation { window.cancelAnimationFrame(frame) }
        }
        currentCoroutineContext().ensureActive()
        // A newer click changes this state immediately, before its recomposition cancels us.
        if (request.attempt == copyAttempt) announcement = request.message
    }

    Div(LCodeBlockStyle.toModifier().then(modifier).toAttrs()) {
        Row(LCodeBlockHeaderStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
            Text(label)
            Div(Modifier.styleModifier { property("margin-inline-start", "auto") }.toAttrs()) {
                if (canCopy) {
                    LIconButton(
                        label = tf("ds.ds.lcode_block.copy_labelled", "Copy {label}", "label" to label),
                        onClick = {
                            val attempt = copyAttempt + 1
                            copyAttempt = attempt
                            copyJob?.cancel()
                            copyJob = scope.launch {
                                val copied = try {
                                    copySafely(rendered)
                                    true
                                } catch (cancelled: CancellationException) {
                                    throw cancelled
                                } catch (_: Throwable) {
                                    false
                                }
                                currentCoroutineContext().ensureActive()
                                if (attempt != copyAttempt) return@launch
                                copyFailed = !copied
                                if (copied) {
                                    announce(attempt, copiedMessage)
                                    confirmation.confirm()
                                    onCopied()
                                } else {
                                    announce(attempt, failedMessage)
                                }
                            }
                        },
                        size = LButtonSize.SM,
                    ) { LCopyGlyph(confirmed = confirmation.confirmed && !copyFailed) }
                }
            }
        }
        // Keep the visible remedy steady while the separate live region resets and refills.
        if (copyFailed) {
            P(
                Modifier
                    .margin(0.px)
                    .padding(leftRight = Space.LG, topBottom = Space.SM)
                    .fontSize(TypeScale.Small)
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .toAttrs(),
            ) { Text(failedMessage) }
        }
        // Mount the empty live region before any result. A newly inserted, already filled region
        // can be silent; the glyph and onCopied callback do not wait for this announcement frame.
        Div(
            LVisuallyHiddenStyle.toModifier().toAttrs {
                attr("role", "status")
                attr("aria-live", "polite")
                attr("aria-atomic", "true")
            },
        ) { Text(announcement) }
        val pre: @Composable () -> Unit = {
            Pre(
                LCodeBlockPreStyle.toModifier()
                    .role("region")
                    .styleModifier {
                        if (lines > maxLines && !expanded) property("max-height", "${maxLines * 1.7}em")
                    }
                    // Named, because `role="region"` without one is dropped from the landmark list
                    // entirely — and this is also a tab stop, so a reader arriving here by keyboard
                    // was told nothing about what they had landed in. `LOneTimeSecret` labels its
                    // own scroller the same way.
                    .toAttrs {
                        attr("tabindex", "0")
                        attr("aria-label", label)
                    },
            ) { Code { Text(rendered) } }
        }
        if (lines > maxLines) {
            TagElement(
                elementBuilder = DetailsElement,
                applyAttrs = Modifier.toAttrs(),
                content = {
                    TagElement(
                        elementBuilder = SummaryElement,
                        applyAttrs = Modifier.toAttrs {
                            attr("aria-expanded", expanded.toString())
                            onClick { expanded = !expanded }
                        },
                        content = { Text(tf("ds.ds.lcode_block.show_full", "Show full {label}", "label" to label)) },
                    )
                    pre()
                },
            )
        } else {
            pre()
        }
    }
}
